import React from 'react';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getClassStatus, STATUS_CATEGORY } from '../utils/classQualityChecks';
import ClassLevelPanel from './ClassLevelPanel';

// Status-chip treatment per category. Ownership drives the color of a genuine issue
// (amber = SeasonManager, blue = Dash, red = mixed); resolved/informational states are quiet
// (outlined, neutral). Unknown is a distinct red-outlined anomaly. Status stays the only loud column.
const STATUS_CHIP = {
  [STATUS_CATEGORY.MISSING_LEVEL]: { color: 'warning', variant: 'filled' },          // amber (SM)
  [STATUS_CATEGORY.AMBIGUOUS_MAPPING]: { color: 'warning', variant: 'filled' },      // amber (SM)
  [STATUS_CATEGORY.INSTRUCTOR_NOT_TRACKED]: { color: 'info', variant: 'filled' },    // blue (Dash)
  [STATUS_CATEGORY.MULTIPLE_ISSUES]: { color: 'error', variant: 'filled' },          // red (mixed)
  [STATUS_CATEGORY.UNKNOWN]: { color: 'error', variant: 'outlined' },                // distinct anomaly
  [STATUS_CATEGORY.AUTOMATICALLY_MAPPED]: { color: 'default', variant: 'outlined' }, // quiet
  [STATUS_CATEGORY.MANUAL_OVERRIDE]: { color: 'default', variant: 'outlined' },      // quiet
  [STATUS_CATEGORY.MANUAL_ASSIGNMENT]: { color: 'default', variant: 'outlined' },    // quiet
};

// Capped instructor display: up to 3 names, then "+N more"; full list on hover / in the panel.
function instructorDisplay(row) {
  const configured = row.instructor_configured === true || row.instructor_configured === 1;
  if (!configured) {
    return { configured: false, text: 'Not tracked', title: 'No instructor configured in Dash for this class.' };
  }
  const full = row.instructor_names || '';
  const names = full ? full.split(', ') : [];
  const count = row.instructor_count || names.length;
  const shown = names.slice(0, 3).join(', ');
  const extra = count > 3 ? ` +${count - 3} more` : '';
  return { configured: true, text: `${shown}${extra}`, title: full };
}

// One class row. Columns: (expand) | Franchise | Facility | Class name | Canonical level |
// Instructor | Status.
export default function ClassAuditRow({ row, levels, onRowUpdate, isExpanded, onToggle, viewRows }) {
  const status = getClassStatus(row);
  const chip = STATUS_CHIP[status.category] || STATUS_CHIP[STATUS_CATEGORY.UNKNOWN];
  const detailId = `class-audit-detail-${row.team_dim_id}`;
  const effectiveLevel = row.effective_level_name || '—';   // quiet; — when absent
  const facility = row.facility_name || '—';
  // Friendly franchise name, falling back to com_code when dim_franchise has no match.
  const franchise = row.franchise_name || row.com_code || '—';
  const instructor = instructorDisplay(row);

  return (
    <>
      <tr>
        <td style={{ width: 40 }}>
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            aria-expanded={isExpanded}
            aria-controls={detailId}
            aria-label={isExpanded ? 'Collapse class details' : 'Expand class details'}
            onClick={onToggle}
          >
            {isExpanded ? '▾' : '▸'}
          </button>
        </td>
        {/* Franchise & Facility wrap rather than truncate, and carry a title tooltip, so auditors
            never have to guess between similarly named locations. */}
        <td className="text-start text-secondary" title={franchise}>{franchise}</td>
        <td className="text-start text-secondary" title={facility}>{facility}</td>
        <td className="text-start">{row.class_name}</td>
        <td className="text-start text-secondary">{effectiveLevel}</td>
        {/* Instructor (Dash-owned, read-only): capped names, or a muted "Not tracked". */}
        <td className="text-start text-secondary" title={instructor.title}>
          {instructor.configured ? instructor.text : <span className="fst-italic">{instructor.text}</span>}
        </td>
        <td className="text-start">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip size="small" label={status.label} color={chip.color} variant={chip.variant} />
            {status.warehouseRefreshPending && (
              // Subtle + SEPARATE: a save that reporting/warehouse reflect on the next dbt
              // refresh. NOT a quality failure.
              <Typography
                component="span"
                variant="caption"
                color="text.disabled"
                sx={{ ml: 1 }}
                title="Saved. Reporting and the warehouse reflect this on the next refresh."
              >
                pending refresh
              </Typography>
            )}
          </Box>
        </td>
      </tr>

      {isExpanded && (
        <tr id={detailId}>
          {/* text-start opts out of the global `td { text-align: center }` in index.css, exactly as
              every sibling cell above already does. Without it the expanded panel's prose centres
              while its MUI controls stay left-aligned. */}
          <td colSpan={7} className="text-start">
            <Box sx={{ px: 2, py: 1.5 }}>
              <ClassLevelPanel row={row} levels={levels} onRowUpdate={onRowUpdate} viewRows={viewRows} />
            </Box>
          </td>
        </tr>
      )}
    </>
  );
}
